import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';
import { FooterHeaderComponent } from '../footer-header/footer-header.component';
import { FooterComponent } from "../footer/footer.component";

interface RegulatoryOption {
  title: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-regularity',
  standalone: true,
  imports: [CommonModule, FooterHeaderComponent, FooterComponent],
  templateUrl: './regularity.component.html',
  styleUrl: './regularity.component.scss',
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px)' }),
        animate('800ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('slideInLeft', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-50px)' }),
        animate('600ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('slideInRight', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(50px)' }),
        animate('600ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('pulse', [
      transition('* => *', [
        animate('500ms ease-in-out', keyframes([
          style({ transform: 'scale(1)', offset: 0 }),
          style({ transform: 'scale(1.05)', offset: 0.5 }),
          style({ transform: 'scale(1)', offset: 1 })
        ]))
      ])
    ]),
    trigger('cardTransition', [
      transition('* => *', [
        animate('400ms ease-in-out', keyframes([
          style({ opacity: 0, transform: 'scale(0.95)', offset: 0 }),
          style({ opacity: 0.5, transform: 'scale(1.02)', offset: 0.5 }),
          style({ opacity: 1, transform: 'scale(1)', offset: 1 })
        ]))
      ])
    ])
  ]
})
export class RegularityComponent implements OnInit, OnDestroy {
  isStandalonePage = false;
  regulatoryOptions: RegulatoryOption[] = [
    {
      title: 'HPS Life Insurance Policy',
      description: 'HPS Life is a life insurance policy, managed on behalf of the insurer, One Family, by HPS Life, a trading name of Health Pulse Services Limited which is regulated and authorised by the Financial Conduct Authority (FRN: 943749).',
      icon: '<path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#fffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 17L12 22L22 17" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 12L12 17L22 12" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    },
    {
      title: 'Registered Office',
      description: 'Westhill House, 1st Floor, 2b Devonshire Road, Bexleyhealth, DA6 8DS.',
      icon: '<path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 22V12H15V22" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    },
    {
      title: 'One Family Assurance',
      description: 'One Family is a trading name of Family Assurance Friendly Society Limited (FAFSL). FAFSL is registered and incorporated under the Friendly Societies Act 1992, registered number 939F. FAFSL is authorised by the Prudential Regulation Authority and regulated by the Financial Conduct Authority and the Prudential Regulation Authority.',
      icon: '<path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    },
    {
      title: 'FAFSL Registration',
      description: 'FAFSL and all its subsidiaries are registered in England & Wales at 16-17 West Street, Brighton, BN1 2RL, United Kingdom.',
      icon: '<path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    },
    {
      title: 'Financial Services Register',
      description: 'You can check our details on the Financial Services Register at www.fca.org.uk/firms/systems-reporting/register or by contacting the Financial Conduct Authority on 0800 111 6768.',
      icon: '<path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 2V8H20" stroke="#0075C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    }
  ];

  private scrollObserver: IntersectionObserver | null = null;

  constructor(private router: Router) { }

  ngOnInit() {
    this.isStandalonePage = !this.router.url.startsWith('/home');
    this.setupScrollAnimations();
  }

  ngOnDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    this.checkScrollAnimations();
  }



  private setupScrollAnimations() {
    // Create intersection observer for scroll animations
    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-on-scroll');
        } else {
          entry.target.classList.remove('animate-on-scroll');
        }
      });
    }, {
      threshold: 0.3,
      rootMargin: '0px 0px -100px 0px'
    });

    // Observe all content cards and icons
    setTimeout(() => {
      const cards = document.querySelectorAll('.content-card');
      const icons = document.querySelectorAll('.item-icon');

      cards.forEach(card => {
        this.scrollObserver?.observe(card);
      });

      icons.forEach(icon => {
        this.scrollObserver?.observe(icon);
      });
    }, 100);
  }

  private checkScrollAnimations() {
    const cards = document.querySelectorAll('.content-card');
    const icons = document.querySelectorAll('.item-icon');

    cards.forEach(card => {
      const rect = card.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Trigger animation when card is visible
      if (rect.top < windowHeight * 0.7 && rect.bottom > windowHeight * 0.3) {
        card.classList.add('animate-on-scroll');
      } else {
        card.classList.remove('animate-on-scroll');
      }
    });

    icons.forEach(icon => {
      const rect = icon.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Trigger animation when icon is visible
      if (rect.top < windowHeight * 0.7 && rect.bottom > windowHeight * 0.3) {
        icon.classList.add('animate-on-scroll');
      } else {
        icon.classList.remove('animate-on-scroll');
      }
    });
  }
}
